import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@src/components';
import './SantaGiftsDashboard.css';

type Dashboard = {
  recipients: number; unopened: number; opened: number; spreadClicked: number; checkoutClicked: number; thanked: number; buyers: number; purchases: number;
  bundles: { size: number | null; buyers: number; purchases: number; checkoutClicked: number }[];
  updatedAt: string;
};

export function SantaGiftsDashboard({ revision, onDenied }: { revision: number; onDenied: () => void }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<Dashboard | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset the visible request status when loading the external dashboard.
    setLoading(true); setError(false);
    void fetch('/api/admin/santa-gifts/dashboard', { credentials: 'same-origin', cache: 'no-store', signal: controller.signal })
      .then(async response => {
        if ([401,403].includes(response.status)) { onDenied(); return; }
        if (!response.ok) throw new Error('Dashboard unavailable');
        const result: Dashboard = await response.json();
        if (!result || typeof result.recipients !== 'number' || !Array.isArray(result.bundles)) throw new Error('Invalid dashboard response');
        if (!controller.signal.aborted) setData(result);
      }).catch(() => { if (!controller.signal.aborted) setError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
    // Authorization callback identity must not restart requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, refresh]);
  const number = (value: number) => value.toLocaleString(i18n.language);
  const percent = (value: number, total: number) => new Intl.NumberFormat(i18n.language, { style:'percent', maximumFractionDigits:1 }).format(total ? value / total : 0);
  const stages = data ? [
    { key:'recipients', value:data.recipients }, { key:'opened', value:data.opened },
    { key:'spreadClicked', value:data.spreadClicked ?? 0 },
  ] : [];
  return <section className="gift-dashboard" aria-labelledby="gift-dashboard-title" aria-busy={loading} data-testid="gift-dashboard">
    <div className="gift-dashboard__header">
      <div><p className="gift-dashboard__eyebrow">{t('admin.giftDashboard.eyebrow')}</p><h2 id="gift-dashboard-title">{t('admin.giftDashboard.title')}</h2></div>
      <Button size="small" variant="secondary" disabled={loading} onClick={() => setRefresh(value => value + 1)}>{t('admin.gifts.refresh')}</Button>
    </div>
    <p className="gift-dashboard__description">{t('admin.giftDashboard.scope')}</p>
    {error && <p role="alert">{t('admin.giftDashboard.error')}</p>}
    {loading && !data && <div className="gift-dashboard__loading"><Spinner label={t('admin.giftDashboard.loading')} /></div>}
    {data && <>
      <div className="gift-dashboard__summary"><span>{t('admin.giftDashboard.unopened')}: <strong>{number(data.unopened)}</strong></span><span>{t('admin.giftDashboard.purchases')}: <strong>{number(data.purchases)}</strong></span></div>
      {!data.recipients && <p role="status">{t('admin.giftDashboard.empty')}</p>}
      <div className="gift-dashboard__grid">
        <div>
          <div className="gift-dashboard__journey">
          <ol className="gift-dashboard__funnel" aria-label={t('admin.giftDashboard.title')}>
            {stages.map((stage, index) => <li key={stage.key} data-testid={`gift-stage-${stage.key}`}>
              <span className="gift-dashboard__step" aria-hidden="true">0{index + 1}</span>
              <div className="gift-dashboard__stage"><div className="gift-dashboard__stage-title"><span>{t(`admin.giftDashboard.${stage.key}`)}</span><strong>{number(stage.value)}</strong><small>{percent(stage.value, data.recipients)}</small></div>
                <div className="gift-dashboard__track" aria-hidden="true"><div style={{width:`${data.recipients ? stage.value/data.recipients*100 : 0}%`}} /></div>
              </div>
            </li>)}
          </ol>
          <div className="gift-dashboard__paths">
            {data.bundles.filter(bundle => [1,3,5].includes(bundle.size ?? 0)).map(bundle => <div className="gift-dashboard__path" key={bundle.size} data-testid={`gift-path-${bundle.size}`}>
              <div className="gift-dashboard__path-card">
                <span className="gift-dashboard__step">04 · {t('admin.giftDashboard.boxes', {count:bundle.size!})}</span>
                <span>{t('admin.giftDashboard.checkoutClicked')}</span>
                <div className="gift-dashboard__path-value"><strong>{number(bundle.checkoutClicked ?? 0)}</strong><small>{percent(bundle.checkoutClicked ?? 0, data.recipients)}</small></div>
                <div className="gift-dashboard__track" aria-hidden="true"><div style={{width:`${data.recipients ? (bundle.checkoutClicked ?? 0)/data.recipients*100 : 0}%`}} /></div>
              </div>
              <span className="gift-dashboard__path-arrow" aria-hidden="true">→</span>
              <div className="gift-dashboard__path-card gift-dashboard__path-card--bought">
                <span className="gift-dashboard__step">05 · {t('admin.giftDashboard.boxes', {count:bundle.size!})}</span>
                <span>{t('admin.giftDashboard.buyers')}</span>
                <div className="gift-dashboard__path-value"><strong>{number(bundle.buyers)}</strong><small>{percent(bundle.buyers, data.recipients)}</small></div>
                <div className="gift-dashboard__track" aria-hidden="true"><div style={{width:`${data.recipients ? bundle.buyers/data.recipients*100 : 0}%`}} /></div>
              </div>
            </div>)}
          </div>
          </div>
          <div className="gift-dashboard__thanks" data-testid="gift-stage-thanked">
            <span className="gift-dashboard__thanks-branch" aria-hidden="true">↳</span>
            <div><span>{t('admin.giftDashboard.thanked')}</span><p className="gift-dashboard__note">{t('admin.giftDashboard.thankNote')}</p></div>
            <strong>{number(data.thanked)}</strong><small>{percent(data.thanked, data.recipients)}</small>
          </div>
          <p className="gift-dashboard__note">{t('admin.giftDashboard.stageNote')}</p>
        </div>
        <p className="gift-dashboard__note">{t('admin.giftDashboard.repeatNote')}</p>
      </div>
      <p className="gift-dashboard__updated">{t('admin.giftDashboard.updated')} <time dateTime={data.updatedAt}>{new Date(data.updatedAt).toLocaleString(i18n.language, {dateStyle:'short',timeStyle:'short'})}</time></p>
    </>}
  </section>;
}
